import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/state/auth";
import { ApiError } from "@/lib/api";
import { Alert, Button, Field, Select, cx } from "@/components/ui";
import { PROVINCES } from "@/lib/format";
import type { Address } from "@/lib/types";
import { AuthLayout } from "./Login";

export function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<0 | 1>(0);
  const [type, setType] = useState<"individual" | "business">("business");
  const [collection, setCollection] = useState<Address>({
    type: "business",
    contact_name: "",
    phone: "",
    street: "",
    suburb: "",
    city: "",
    province: "Gauteng",
    postal_code: "",
  });
  const [form, setForm] = useState({
    company_name: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    password: "",
  });
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [key]: event.target.value }));

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setFields({});
    setBusy(true);
    try {
      await register({
        account_type: type,
        ...form,
        collection_address: {
          ...collection,
          // Default the contact to the person signing up: the carrier needs a
          // name at the door and asking twice for the same one is friction.
          contact_name: collection.contact_name || `${form.first_name} ${form.last_name}`.trim(),
          phone: collection.phone || form.phone,
        },
      });
      navigate("/app");
    } catch (err) {
      if (err instanceof ApiError) {
        setFields(err.fields);
        // Field errors on the address belong to the second step, so send the
        // person to the step that holds the problem rather than to a form that
        // looks fine.
        if (Object.keys(err.fields).some((key) => key.startsWith("collection_address"))) {
          setStep(1);
        } else if (Object.keys(err.fields).length > 0) {
          setStep(0);
        }
        // Field-level errors are shown inline; only say something at the top
        // if there is nothing to attach to a specific input.
        if (Object.keys(err.fields).length === 0) setError(err.message);
      } else {
        setError("Could not create your account. Try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Create your account" subtitle="Two minutes, no card required.">
      <ol className="mb-6 flex gap-2 text-sm">
        {["Your details", "Collection address"].map((label, index) => (
          <li
            key={label}
            className={cx(
              "rounded-lg px-3 py-1.5 font-medium",
              index === step ? "bg-brand-soft text-brand" : "text-ink-400",
            )}
          >
            {index + 1}. {label}
          </li>
        ))}
      </ol>

      <form onSubmit={submit} className="space-y-4" noValidate>
        {error && <Alert>{error}</Alert>}

        <div className={cx(step === 1 && "hidden")}>
          <span className="ws-label">Account type</span>
          <div className="grid grid-cols-2 gap-2">
            {(["individual", "business"] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setType(option)}
                className={cx(
                  "rounded-xl border px-3 py-2.5 text-sm font-medium capitalize transition",
                  type === option
                    ? "border-brand bg-brand-soft text-brand"
                    : "border-ink-200 bg-white text-ink-600 hover:bg-ink-100",
                )}
              >
                {option}
              </button>
            ))}
          </div>
        </div>

        {type === "business" && step === 0 && (
          <Field
            label="Company name"
            name="company_name"
            value={form.company_name}
            onChange={set("company_name")}
            error={fields.company_name}
          />
        )}

        <div className={cx("grid grid-cols-2 gap-3", step === 1 && "hidden")}>
          <Field label="First name" name="first_name" value={form.first_name} onChange={set("first_name")} error={fields.first_name} />
          <Field label="Last name" name="last_name" value={form.last_name} onChange={set("last_name")} error={fields.last_name} />
        </div>

        <div className={cx(step === 1 && "hidden")}>
        <Field label="Email address" name="email" type="email" autoComplete="email" value={form.email} onChange={set("email")} error={fields.email} />
        <Field label="Mobile number" name="phone" inputMode="tel" value={form.phone} onChange={set("phone")} error={fields.phone} hint="Used for collection and delivery notifications." />
        <Field
          label="Password"
          name="password"
          type="password"
          autoComplete="new-password"
          value={form.password}
          onChange={set("password")}
          error={fields.password}
          hint="At least 10 characters."
        />
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <p className="text-sm text-ink-500">
              Where should we collect your parcels? This becomes your default pickup address and is
              what the courier is given on every booking. You can add more later.
            </p>
            <Field
              label="Street address"
              value={collection.street}
              onChange={(e) => setCollection({ ...collection, street: e.target.value })}
              error={fields["collection_address.street"]}
            />
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Suburb"
                value={collection.suburb}
                onChange={(e) => setCollection({ ...collection, suburb: e.target.value })}
                error={fields["collection_address.suburb"]}
              />
              <Field
                label="City"
                value={collection.city}
                onChange={(e) => setCollection({ ...collection, city: e.target.value })}
                error={fields["collection_address.city"]}
              />
              <Select
                label="Province"
                value={collection.province}
                onChange={(e) => setCollection({ ...collection, province: e.target.value })}
                error={fields["collection_address.province"]}
                options={PROVINCES.map((p) => ({ value: p, label: p }))}
              />
              <Field
                label="Postal code"
                inputMode="numeric"
                maxLength={4}
                value={collection.postal_code}
                onChange={(e) => setCollection({ ...collection, postal_code: e.target.value })}
                error={fields["collection_address.postal_code"]}
              />
            </div>
            <Select
              label="Address type"
              value={collection.type ?? "business"}
              onChange={(e) =>
                setCollection({ ...collection, type: e.target.value as Address["type"] })
              }
              options={[
                { value: "business", label: "Business premises" },
                { value: "residential", label: "Residential" },
              ]}
            />
            <Button type="button" variant="ghost" className="px-0" onClick={() => setStep(0)}>
              Back to your details
            </Button>
          </div>
        )}

        {step === 0 ? (
          <Button
            type="button"
            className="w-full"
            disabled={!form.first_name || !form.email || !form.password}
            onClick={() => setStep(1)}
          >
            Continue
          </Button>
        ) : (
          <Button type="submit" loading={busy} className="w-full">
            Create account
          </Button>
        )}
      </form>

      <p className="mt-6 text-center text-sm text-ink-400">
        Already have an account?{" "}
        <Link to="/login" className="font-medium text-brand">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
